<template>
    <div class="p-2" style="min-height: 400px;">
        <div v-if="!under_construction" class="container-fluid px-0">
            <div class="mb-4">
                <h4 class="ctc-dashboard-title mb-1">Dashboard for {{ currentYear }}</h4>
                <div class="ctc-dashboard-subtitle">Overview of CTC Configuration &amp; Calculations</div>
            </div>

            <div class="row align-items-stretch">
                <!-- Left side: 6 Metric Cards -->
                <div class="col-lg-9 col-md-8 d-flex flex-column justify-content-between">
                    <!-- Row 1 -->
                    <div class="row">
                        <!-- Card 1 -->
                        <div class="col-md-4 mb-4">
                            <div class="card h-100 dashboard-card w-100">
                                <div class="card-body">
                                    <div class="d-flex justify-content-between align-items-start mb-3">
                                        <span class="card-metric-title">Total Employees</span>
                                        <div class="icon-badge badge-blue">
                                            <!-- Users -->
                                            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                                                <mask id="ctcIcoPeople">
                                                    <rect width="24" height="24" fill="#fff"/>
                                                    <path fill="#000" d="M-1.2 22.6C-1.2 17 3.6 12.5 9.47 12.5C15.34 12.5 20.14 17 20.14 22.6L20.14 24.5L-1.2 24.5Z"/>
                                                </mask>
                                                <g mask="url(#ctcIcoPeople)">
                                                    <circle cx="19.09" cy="10.47" r="2.43"/>
                                                    <path d="M15.4 18.06C15.4 15.87 16.9 14.09 19.65 14.09C22.4 14.09 23.9 15.87 23.9 18.06L23.9 18.16A.4.4 0 0 1 23.5 18.56L15.8 18.56A.4.4 0 0 1 15.4 18.16Z"/>
                                                </g>
                                                <circle cx="9.33" cy="6.85" r="4.86"/>
                                                <path d="M0 21.4C0 17.2 4.24 13.71 9.47 13.71C14.7 13.71 18.94 17.2 18.94 21.4L18.94 21.5A.5.5 0 0 1 18.44 22L.5 22A.5.5 0 0 1 0 21.5Z"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <h4 class="card-metric-value mb-1">{{ total_employees }}</h4>
                                    <span class="card-metric-desc">Active employees</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Card 2 -->
                        <div class="col-md-4 mb-4">
                            <div class="card h-100 dashboard-card w-100">
                                <div class="card-body">
                                    <div class="d-flex justify-content-between align-items-start mb-3">
                                        <span class="card-metric-title">Active CTC Items</span>
                                        <div class="icon-badge badge-green">
                                            <!-- Gear: the 1px stroke rounds every tooth corner, so the
                                                 path radii below are drawn 0.5 units short of the design. -->
                                            <svg width="27" height="27" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                <path fill="currentColor" fill-rule="evenodd" stroke="currentColor" stroke-width="1" stroke-linejoin="round" d="M20.55 12.9 L22.85 13.82 A11 11 0 0 1 20.96 18.39 L18.68 17.41 A8.6 8.6 0 0 1 17.41 18.68 L18.39 20.96 A11 11 0 0 1 13.82 22.85 L12.9 20.55 A8.6 8.6 0 0 1 11.1 20.55 L10.18 22.85 A11 11 0 0 1 5.61 20.96 L6.59 18.68 A8.6 8.6 0 0 1 5.32 17.41 L3.04 18.39 A11 11 0 0 1 1.15 13.82 L3.45 12.9 A8.6 8.6 0 0 1 3.45 11.1 L1.15 10.18 A11 11 0 0 1 3.04 5.61 L5.32 6.59 A8.6 8.6 0 0 1 6.59 5.32 L5.61 3.04 A11 11 0 0 1 10.18 1.15 L11.1 3.45 A8.6 8.6 0 0 1 12.9 3.45 L13.82 1.15 A11 11 0 0 1 18.39 3.04 L17.41 5.32 A8.6 8.6 0 0 1 18.68 6.59 L20.96 5.61 A11 11 0 0 1 22.85 10.18 L20.55 11.1 A8.6 8.6 0 0 1 20.55 12.9 Z M5.21 12 A6.79 6.79 0 1 0 18.79 12 A6.79 6.79 0 1 0 5.21 12 Z"/>
                                                <circle cx="12" cy="12" r="3.44" fill="none" stroke="currentColor" stroke-width="1.68"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <h4 class="card-metric-value mb-1">{{ active_ctc_items }}</h4>
                                    <span class="card-metric-desc">Configured benefit items</span>
                                </div>
                            </div>
                        </div>

                        <!-- Card 3 -->
                        <div class="col-md-4 mb-4">
                            <div class="card h-100 dashboard-card w-100">
                                <div class="card-body">
                                    <div class="d-flex justify-content-between align-items-start mb-3">
                                        <span class="card-metric-title">Total Fixed CTC</span>
                                        <div class="icon-badge badge-purple">
                                            <span class="taka-symbol">৳</span>
                                        </div>
                                    </div>
                                    <h4 class="card-metric-value mb-1">{{ formatAmount(total_fixed_ctc) }}</h4>
                                    <span class="card-metric-desc">Annual projected cost (12 months)</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2 -->
                    <div class="row">
                        <!-- Card 4 -->
                        <div class="col-md-4 mb-4">
                            <div class="card h-100 dashboard-card w-100">
                                <div class="card-body">
                                    <div class="d-flex justify-content-between align-items-start mb-3">
                                        <span class="card-metric-title">Total Actual CTC</span>
                                        <div class="icon-badge badge-blue">
                                            <!-- Trend line -->
                                            <svg width="32" height="15" viewBox="0 0 25.6 12" fill="none" stroke="currentColor" stroke-width="1.62" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
                                                <path d="M.8 10.8 7.8 5.6 13.4 11.2 24.8.8"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <h4 class="card-metric-value mb-1">{{ formatAmount(total_actual_ctc) }}</h4>
                                    <span class="card-metric-desc">Pro-rated based on tenure</span>
                                </div>
                            </div>
                        </div>

                        <!-- Card 5 -->
                        <div class="col-md-4 mb-4">
                            <div class="card h-100 dashboard-card w-100">
                                <div class="card-body">
                                    <div class="d-flex justify-content-between align-items-start mb-3">
                                        <span class="card-metric-title">Avg Fixed CTC</span>
                                        <div class="icon-badge badge-green">
                                            <!-- Calendar: the mask knocks the page area and the two
                                                 binding-ring gaps out of the solid frame. -->
                                            <svg width="29" height="29" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                                                <mask id="ctcIcoCal">
                                                    <rect width="24" height="24" fill="#fff"/>
                                                    <rect x="1.4" y="7.97" width="21.2" height="14.56" rx=".5" fill="#000"/>
                                                    <rect x="4.01" y="-1" width="4.06" height="7.18" rx="2.03" fill="#000"/>
                                                    <rect x="16.17" y="-1" width="4.06" height="7.18" rx="2.03" fill="#000"/>
                                                </mask>
                                                <rect mask="url(#ctcIcoCal)" y="1.97" width="24" height="21.95" rx="1.64"/>
                                                <rect x="5.01" width="2.06" height="5.18" rx="1.03"/>
                                                <rect x="17.17" width="2.06" height="5.18" rx="1.03"/>
                                                <rect x="12.7" y="10.2" width="3" height="2.7" rx=".35"/>
                                                <rect x="17.3" y="10.2" width="3" height="2.7" rx=".35"/>
                                                <rect x="3.6" y="14.2" width="3" height="2.7" rx=".35"/>
                                                <rect x="8.2" y="14.2" width="3" height="2.7" rx=".35"/>
                                                <rect x="12.7" y="14.2" width="3" height="2.7" rx=".35"/>
                                                <rect x="17.3" y="14.2" width="3" height="2.7" rx=".35"/>
                                                <rect x="3.6" y="18.1" width="3" height="2.7" rx=".35"/>
                                                <rect x="8.2" y="18.1" width="3" height="2.7" rx=".35"/>
                                                <rect x="12.7" y="18.1" width="3" height="2.7" rx=".35"/>
                                                <rect x="17.3" y="18.1" width="3" height="2.7" rx=".35"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <h4 class="card-metric-value mb-1">{{ formatAmount(avg_fixed_ctc) }}</h4>
                                    <span class="card-metric-desc">Per employee annually</span>
                                </div>
                            </div>
                        </div>

                        <!-- Card 6 -->
                        <div class="col-md-4 mb-4">
                            <div class="card h-100 dashboard-card w-100">
                                <div class="card-body">
                                    <div class="d-flex justify-content-between align-items-start mb-3">
                                        <span class="card-metric-title">Avg Actual CTC</span>
                                        <div class="icon-badge badge-purple">
                                            <!-- Calendar + clock: the calendar is solid here and its
                                                 day cells, header rule and the clock gap are masked out. -->
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                                                <mask id="ctcIcoCalClock">
                                                    <rect width="24" height="24" fill="#fff"/>
                                                    <rect x="-1" y="7.15" width="22" height="1.05" fill="#000"/>
                                                    <rect x="2.68" y="-1" width="4.92" height="6.29" rx="2.46" fill="#000"/>
                                                    <rect x="12.51" y="-1" width="4.92" height="6.29" rx="2.46" fill="#000"/>
                                                    <rect x="3.28" y="10.06" width="3.2" height="2.91" rx=".6" fill="#000"/>
                                                    <rect x="8.2" y="10.06" width="3.2" height="2.91" rx=".6" fill="#000"/>
                                                    <rect x="13.11" y="10.06" width="3.2" height="2.91" rx=".6" fill="#000"/>
                                                    <rect x="3.28" y="15.43" width="3.2" height="3.05" rx=".6" fill="#000"/>
                                                    <rect x="8.2" y="15.43" width="3.2" height="3.05" rx=".6" fill="#000"/>
                                                    <circle cx="18.67" cy="18.74" r="6.29" fill="#000"/>
                                                </mask>
                                                <mask id="ctcIcoClock">
                                                    <rect x="12" y="12" width="12" height="12" fill="#fff"/>
                                                    <path d="M18.67 15.14V18.74L20.47 20.44" fill="none" stroke="#000" stroke-width=".85" stroke-linecap="round" stroke-linejoin="round"/>
                                                </mask>
                                                <rect mask="url(#ctcIcoCalClock)" y="1.64" width="20.12" height="19.6" rx="1.49"/>
                                                <rect x="3.8" width="2.68" height="4.17" rx="1.34"/>
                                                <rect x="13.63" width="2.68" height="4.17" rx="1.34"/>
                                                <circle mask="url(#ctcIcoClock)" cx="18.67" cy="18.74" r="5.25"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <h4 class="card-metric-value mb-1">{{ formatAmount(avg_actual_ctc) }}</h4>
                                    <span class="card-metric-desc">Per employee (actual)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right side: System Features -->
                <div class="col-lg-3 col-md-4 mb-4">
                    <div class="system-features-card">
                        <div class="features-header">
                            <b-icon icon="info-circle" class="mr-2"></b-icon>
                            <span>System Features</span>
                        </div>
                        <div class="features-body">
                            <div class="features-section-title">Configuration:</div>
                            <ul class="features-list">
                                <li>Rule-based employee eligibility</li>
                                <li>Department &amp; Custom filtering</li>
                                <li>Multiple payment frequencies</li>
                                <li>Active/Inactive status management</li>
                            </ul>
                            <div class="features-section-title">Configuration:</div>
                            <ul class="features-list">
                                <li>Fixed CTC: 12-month projection</li>
                                <li>Actual CTC: Pro-rated by tenure</li>
                                <li>Quarterly eligibility rules</li>
                                <li>BDT &amp; USD currency support</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="h-100 d-flex align-items-center justify-content-center">
            <div class="text-center p-5 card border-0 shadow-sm bg-light" style="max-width: 600px; border-radius: 16px;">
                <div class="mb-4">
                    <div class="icon-pulse d-inline-block p-4 rounded-circle bg-white shadow-sm mb-3">
                        <b-icon icon="tools" font-scale="4" class="text-primary"></b-icon>
                    </div>
                </div> 
                <h2 class="font-weight-bold text-dark mb-3">Coming Soon...</h2>
                <p class="text-secondary mb-4 px-md-5">
                    We're currently building advanced CTC analytics and deep-dive reporting features for this module.
                </p>
                <div class="px-5 mb-4">
                    <div class="progress rounded-pill" style="height: 8px; background-color: #e9ecef;">
                        <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary rounded-pill" role="progressbar" style="width: 65%"></div>
                    </div>
                </div>
                <div class="d-flex justify-content-center">
                    <span class="badge badge-soft-primary px-3 py-2 rounded-pill">
                        <b-icon icon="clock" class="mr-1"></b-icon> Coming Soon
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import ctcOverlay from '../js/ctcOverlay';

    export default {
        mixins: [ctcOverlay],
        name: "Dashboard",
        watch: {
            loading(newVal) {
                this.ctcOverlay(newVal);
            }
        },
        data() {
            return {
                under_construction: false,
                loading: false,
                total_employees: 0,
                active_ctc_items: 0,
                total_fixed_ctc: 0,
                total_actual_ctc: 0,
                avg_fixed_ctc: 0,
                avg_actual_ctc: 0,
                decimalPointSettings: 'no',
                currentYear: new Date().getFullYear()
            };
        },
        mounted() {
            this.loadDashboardData();
        },
        methods: {
            loadDashboardData() {
                this.loading = true;
                axios.get('/cost-to-company/dashboard')
                    .then(response => {
                        const data = response.data;
                        this.total_employees = data.total_employees || 0;
                        this.active_ctc_items = data.active_ctc_items || 0;
                        this.total_fixed_ctc = data.total_fixed_ctc || 0;
                        this.total_actual_ctc = data.total_actual_ctc || 0;
                        this.avg_fixed_ctc = data.avg_fixed_ctc || 0;
                        this.avg_actual_ctc = data.avg_actual_ctc || 0;
                        if (data.decimal_point_settings) {
                            this.decimalPointSettings = data.decimal_point_settings;
                        }
                        if (data.year) {
                            this.currentYear = data.year;
                        }
                    })
                    .catch(error => {
                        console.error('Error loading dashboard data:', error);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },
            // Figures are shown in full rather than abbreviated to K/M so the
            // exact amount is readable on the card. Decimal places follow the
            // company setting: 'yes' → 2 decimals, 'no' (default) → rounded.
            formatAmount(value) {
                let num = parseFloat(value);
                if (value === null || value === undefined || isNaN(num)) num = 0;
                if (this.decimalPointSettings === 'yes') {
                    return '৳' + num.toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    });
                }
                return '৳' + Math.round(num).toLocaleString('en-IN');
            }
        }
    }
</script>

<style scoped>
.card-metric-desc {
    font-size: 0.75rem;
    color: #94a3b8;
    display: block;
    line-height: 1.3;
    margin-top: 2px;
}
/* Badge size, radius and the three colour pairs below come straight from the
   Figma dashboard spec; the icons inside are drawn with currentColor. */
.icon-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 47px;
    height: 47px;
    border-radius: 11px;
}
.badge-blue {
    background-color: #c9f5ff;
    color: #007aff;
}
.badge-green {
    background-color: #d3f5f2;
    color: #01524b;
}
.badge-purple {
    background-color: #e6e8ff;
    color: #686ccc;
}
.taka-symbol {
    font-size: 28px;
    font-weight: 600;
    font-style: normal;
    line-height: 1;
}
/* .ctc-dashboard-title / .ctc-dashboard-subtitle are shared by every CTC page
   and live in resources/sass/_ctc.scss. */
.system-features-card {
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    background-color: #ffffff;
    overflow: hidden;
    height: 100%;
}
.features-header {
    background-color: #01524B;
    color: #ffffff;
    padding: 12px 16px;
    font-weight: 700;
    font-size: 1rem;
    display: flex;
    align-items: center;
}
.features-header .b-icon {
    font-size: 1.2rem;
}
.features-body {
    padding: 16px;
}
.features-section-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #0f172a;
    margin-top: 8px;
    margin-bottom: 8px;
}
.features-list {
    padding-left: 16px;
    margin-bottom: 16px;
}
.features-list li {
    font-size: 0.8rem;
    color: #000;
    margin-bottom: 6px;
    line-height: 1.4;
}
.icon-pulse {
    animation: pulse 2s infinite;
}
@keyframes pulse {
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.4);
    }
    70% {
        transform: scale(1);
        box-shadow: 0 0 0 15px rgba(0, 123, 255, 0);
    }
    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
    }
}
.badge-soft-primary {
    background-color: rgba(0, 123, 255, 0.1);
    color: #007bff;
}
</style>
